import { FC, lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { MasterLayout } from "../../_metronic/layout/MasterLayout";
import TopBarProgress from "react-topbar-progress-indicator";
import { getCSSVariableValue } from "../../_metronic/assets/ts/_utils";
import { WithChildren } from "../../_metronic/helpers";
import PartnerUpload from "../modules/apps/academies-partners/partnersModal";
import { VirtualRouteGuard } from "./PermissionGuard";
import {
  getAuthUser,
  getDefaultRoute,
  isPortalAdmin,
  isVirtualUser,
} from "../../utils/permissions";

const PrivateRoutes = () => {
  const user = getAuthUser();
  const isAdmin = isPortalAdmin(user);
  const defaultRoute = getDefaultRoute(user);
  
  const UserPage = lazy(() => import("../modules/apps/users/UserPage"));
  const NewsLetterPage = lazy(() => import("../modules/apps/news-letters/NewsLetterPage"));
  const SubscriptionPage = lazy(() => import("../modules/apps/subscriptions/SubscriptionPage"));
  const PlanPage = lazy(() => import("../modules/apps/plans/PlanPage"));
  const ContactPage = lazy(() => import("../modules/apps/contacts/ContactPage"));
  const VideoPage = lazy(() => import("../modules/apps/videos/VideosPage"));
  const SettingsPage = lazy(() => import("../modules/apps/settings/SettingsPage"));
  const DashboardPage = lazy(() => import("../modules/apps/dashboard/DashboardPage"));
  const QuickTransferPage = lazy(() => import("../modules/apps/quick-transfers/QuickTransfersPage"));
  const MonitorsPage = lazy(() => import("../modules/apps/monitors"));
  const DevicesPage = lazy(() => import("../modules/apps/devices/DevicesPage"));
  const WelcomeEmailLogsPage = lazy(() => import("../modules/apps/welcome-email-logs/WelcomeEmailLogsPage"));
  const WelcomeSmsLogsPage = lazy(() => import("../modules/apps/welcome-sms-logs/WelcomeSmsLogsPage"));
  const TrackerPairingPage = lazy(() => import("../modules/apps/tracker-pairing"));
  const VirtualAccountPage = lazy(() => import("../modules/apps/virtual-accounts"));
  const VinErrorReportsPage = lazy(() => import("../modules/apps/vin-error-reports/VinErrorReportsPage"));
  const SoldReportsPage = lazy(() => import("../modules/apps/sold-reports/SoldReportsPage"));
  const KeyperAssetsPage = lazy(() => import("../modules/apps/keyper/assets/KeyperAssetsPage"));
  const KeyperSystemsPage = lazy(() => import("../modules/apps/keyper/systems/KeyperSystemsPage"));
  const RolesPage = lazy(() => import("../modules/apps/roles-management/RolesPage"));
  const UserSettingsPage = lazy(() => import("../modules/settings/SettingsWrapper"));
  const AccessDeniedPage = lazy(
    () => import("../modules/apps/access-denied/AccessDeniedPage")
  );



  return (
    <>
      <Routes>
        <Route element={<MasterLayout />}>
          <Route
            path="access-denied"
            element={
              <SuspensedView>
                <AccessDeniedPage />
              </SuspensedView>
            }
          />
          <Route path="auth/*" element={<Navigate to={defaultRoute} />} />
          <Route
            path="users/*"
            element={
              <SuspensedView>
                <VirtualRouteGuard moduleKey="accounts">
                  <UserPage />
                </VirtualRouteGuard>
              </SuspensedView>
            }
          />
          <Route path="*" element={<Navigate to={defaultRoute} />} />

          <Route
            path="news-letters/*"
            element={
              <SuspensedView>
                <NewsLetterPage />
              </SuspensedView>
            }
          />
          <Route path="*" element={<Navigate to="/news-letters/list" />} />

          <Route 
           path= "academies-partners/*"
           element= {
            <SuspensedView>
                <PartnerUpload />
              </SuspensedView>
           }
           />

          <Route
            path="subscriptions/*"
            element={
              <SuspensedView>
                <SubscriptionPage />
              </SuspensedView>
            }
          />
          <Route path="*" element={<Navigate to="/subscriptions/list" />} />

          <Route
            path="plans/*"
            element={
              <SuspensedView>
                <PlanPage />
              </SuspensedView>
            }
          />
          <Route path="*" element={<Navigate to="/plans/list" />} />

          <Route
            path="contacts/*"
            element={
              <SuspensedView>
                <ContactPage />
              </SuspensedView>
            }
          />
          <Route path="*" element={<Navigate to="/contacts/list" />} />

          <Route
            path="quickPairImeis/*"
            element={
              <SuspensedView>
                <VirtualRouteGuard moduleKey="quick_pair_imeis">
                  <VideoPage />
                </VirtualRouteGuard>
              </SuspensedView>
            }
          />
          <Route path="*" element={<Navigate to="/quickPairImeis/list" />} />
          <Route
            path="quickTransfers/*"
            element={
              <SuspensedView>
                <VirtualRouteGuard moduleKey="quick_transfers">
                  <QuickTransferPage />
                </VirtualRouteGuard>
              </SuspensedView>
            }
          />
          <Route path="*" element={<Navigate to="/quickTransfers/list" />} />
          <Route
            path="settings/*"
            element={
              <SuspensedView>
                <SettingsPage />
              </SuspensedView>
            }
          />
          <Route path="*" element={<Navigate to="/settings/list" />} />

          <Route
            path="monitors"
            element={
              <SuspensedView>
                <VirtualRouteGuard moduleKey="monitor">
                  <MonitorsPage />
                </VirtualRouteGuard>
              </SuspensedView>
            }
          />

          <Route
            path="tracker-pairing"
            element={
              <SuspensedView>
                <VirtualRouteGuard moduleKey="tracker_pairing">
                  <TrackerPairingPage />
                </VirtualRouteGuard>
              </SuspensedView>
            }
          />

          {!isVirtualUser(user) && (
            <Route
              path="virtual-accounts"
              element={
                <SuspensedView>
                  <VirtualAccountPage />
                </SuspensedView>
              }
            />
          )}

          {isAdmin && (
            <Route
              path="vin-error-reports"
              element={
                <SuspensedView>
                  <VinErrorReportsPage />
                </SuspensedView>
              }
            />
          )}

          <Route
            path="sold-reports"
            element={
              <SuspensedView>
                <VirtualRouteGuard moduleKey="sold_reports">
                  <SoldReportsPage />
                </VirtualRouteGuard>
              </SuspensedView>
            }
          />

          
          {isAdmin && (
            <Route
              path="keyper/assets"
              element={
                <SuspensedView>
                  <KeyperAssetsPage />
                </SuspensedView>
              }
            />
          )}

          {isAdmin && (
            <Route
              path="keyper/systems"
              element={
                <SuspensedView>
                  <KeyperSystemsPage />
                </SuspensedView>
              }
            />
          )}

          <Route
            path="devices/*"
            element={
              <SuspensedView>
                <VirtualRouteGuard moduleKey="devices">
                  <DevicesPage />
                </VirtualRouteGuard>
              </SuspensedView>
            }
          />
          <Route path="*" element={<Navigate to="/devices/list" />} />

          {isAdmin && (
          <Route
            path="welcomeEmailLogs/*"
            element={
              <SuspensedView>
                <WelcomeEmailLogsPage />
              </SuspensedView>
            }
          />
          )}

          {isAdmin && (
          <Route
            path="welcomeSmsLogs/*"
            element={
              <SuspensedView>
                <WelcomeSmsLogsPage />
              </SuspensedView>
            }
          />
          )}

          {isAdmin && (
          <Route
            path="rolesManagement/*"
            element={
              <SuspensedView>
                <RolesPage />
              </SuspensedView>
            }
          />
          )}
        
          <Route
            path="dashboard/*"
            element={
              <SuspensedView>
                <DashboardPage />
              </SuspensedView>
            }
          />
          <Route path="*" element={<Navigate to="/dashboard/detail" />} />

          <Route
            path="users/settings"
            element={
              <SuspensedView>
                <UserSettingsPage />
              </SuspensedView>
            }
          />

        </Route>
      </Routes>
    </>
  );
};

const SuspensedView: FC<WithChildren> = ({ children }) => {
  const baseColor = getCSSVariableValue("--bs-primary");
  TopBarProgress.config({
    barColors: {
      "0": baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  });
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>;
};

export { PrivateRoutes };
